(() => {
  const init = (root) => {
    if (!root || root.dataset.arCollectionReady === 'true') return;
    root.dataset.arCollectionReady = 'true';

    const form = root.querySelector('[data-ar-filter-form]');
    const panel = root.querySelector('[data-ar-filter-panel]');
    const backdrop = root.querySelector('.arCollection__backdrop');
    const openButton = root.querySelector('[data-ar-open-filter]');
    const closeButtons = root.querySelectorAll('[data-ar-close-filter]');
    const sort = root.querySelector('[data-ar-sort]');
    const mobile = window.matchMedia('(max-width: 989px)');
    let submitTimer;

    const setLoading = () => root.classList.add('is-loading');

    const closePanel = () => {
      if (!panel) return;
      panel.classList.remove('is-open');
      backdrop?.classList.remove('is-open');
      openButton?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('arCollection-lock');
    };

    const openPanel = () => {
      if (!panel) return;
      panel.classList.add('is-open');
      backdrop?.classList.add('is-open');
      openButton?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('arCollection-lock');
      window.setTimeout(() => panel.querySelector('summary, input, button, a')?.focus(), 180);
    };

    openButton?.addEventListener('click', openPanel);
    closeButtons.forEach((button) => button.addEventListener('click', closePanel));

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && panel?.classList.contains('is-open')) closePanel();
    });

    mobile.addEventListener?.('change', (event) => {
      if (!event.matches) closePanel();
    });

/* AR canonical platform filter — normalizes both clean and legacy Search & Discovery indexes */
    const canonicalizePlatformFilter = () => {
      const cleanPlatformGroup = root.querySelector('[data-ar-filter-group][data-ar-filter-param*="custom.ar_filter_platform"]');
      const legacyPlatformGroup = root.querySelector('[data-ar-filter-group][data-ar-filter-param*="custom.platform"]');
      const platformGroup = cleanPlatformGroup || legacyPlatformGroup;
      if (!platformGroup) return;

      const rawOptions = Array.from(platformGroup.querySelectorAll('[data-ar-filter-option]'));
      const rawInputs = rawOptions.map((option) => option.querySelector('input[type="checkbox"][data-ar-filter-input]')).filter(Boolean);
      if (!rawInputs.length) return;

      const assetData = root.querySelector('[data-ar-platform-assets]')?.dataset || {};
      const definitions = [
        ['Xbox', 'xbox', true],
        ['PlayStation', 'playstation', true],
        ['PC', 'pc', true],
        ['Nintendo', 'nintendo', true],
        ['Mobile', 'mobile', true],
        ['Roblox', '', false],
        ['iTunes', 'itunes', false],
        ['Netflix', 'netflix', false],
        ['Deezer', 'deezer', false],
        ['DirecTV Go', 'directvgo', false],
        ['Amazon Prime', 'amazonprime', false],
        ['Microsoft', 'microsoft', false],
        ['McAfee', '', false],
        ['Uber', '', false],
        ['Win Sports', '', false],
        ['ViX', 'vix', false]
      ];
      const definitionMap = new Map(definitions.map(([label, assetKey, invert], index) => [label, {
        label,
        asset: assetKey ? assetData[assetKey] : '',
        invert,
        index
      }]));

      const add = (list, key) => {
        if (key && !list.includes(key)) list.push(key);
      };

      const keysForLabel = (source) => {
        const raw = (source || '').trim();
        const label = raw.toLowerCase().replace(/\s+/g, ' ');
        const keys = [];

        // Branded services first.
        if (label.includes('itunes') || label.includes('apple')) add(keys, 'iTunes');
        if (label.includes('netflix')) add(keys, 'Netflix');
        if (label === 'deezer' || label.includes('deezer ')) add(keys, 'Deezer');
        if (label.includes('directv')) add(keys, 'DirecTV Go');
        if (label.includes('amazon prime')) add(keys, 'Amazon Prime');
        if (label.includes('microsoft 365') || label.includes('office 365') || label === 'microsoft') add(keys, 'Microsoft');
        if (label.includes('mcafee')) add(keys, 'McAfee');
        if (label === 'uber' || label.includes('uber ')) add(keys, 'Uber');
        if (label.includes('win sports') || label.includes('winsports') || label.includes('win sport')) add(keys, 'Win Sports');
        if (label === 'vix' || label.includes('vix premium')) add(keys, 'ViX');
        if (label.includes('roblox')) add(keys, 'Roblox');

        // Platform families. Generations and sub-platforms collapse into one option.
        if (label.includes('xbox')) add(keys, 'Xbox');
        if (label.includes('playstation') || label.includes('play station') || label.includes('psn') || /(^|\W)ps[45]?(\W|$)/.test(label)) add(keys, 'PlayStation');
        if (label.includes('nintendo') || label.includes('switch')) add(keys, 'Nintendo');
        if (
          label === 'pc' ||
          label.includes(' pc') ||
          label.startsWith('pc') ||
          label.includes('windows') ||
          label.includes('battle.net') ||
          label.includes('steam') ||
          label.includes('rockstar games launcher') ||
          label.includes('chromebook') ||
          label.includes('handheld') ||
          label.includes('steam deck') ||
          label.includes('razer gold') ||
          label.includes('rixty')
        ) add(keys, 'PC');
        if (
          label.includes('mobile') ||
          label.includes('movil') ||
          label.includes('móvil') ||
          label.includes('android') ||
          label.includes('ios') ||
          label.includes('amazon fire') ||
          label.includes('free fire') ||
          label.includes('garena') ||
          label.includes('imvu')
        ) add(keys, 'Mobile');

        // If a stale value is a branded game/service that belongs to a parent platform,
        // remove the obsolete child name by returning only the canonical parent.
        if (label.includes('free fire') || label.includes('garena') || label.includes('imvu')) return ['Mobile'];
        if (label.includes('razer gold') || label.includes('rixty') || label.includes('handheld')) return ['PC'];

        if (!keys.length && raw) add(keys, raw);
        return keys;
      };

      const groups = new Map();
      const ensureGroup = (key) => {
        if (!groups.has(key)) {
          const base = definitionMap.get(key) || { label: key, asset: '', invert: false, index: 999 };
          groups.set(key, { ...base, raw: [], count: 0 });
        }
        return groups.get(key);
      };

      rawOptions.forEach((option) => {
        const input = option.querySelector('input[type="checkbox"][data-ar-filter-input]');
        const label = option.querySelector('.arCollection__optionLabel')?.textContent?.trim() || input?.value || '';
        const count = Number.parseInt(option.querySelector('.arCollection__optionCount')?.textContent || '0', 10) || 0;

        keysForLabel(label).forEach((key) => {
          const group = ensureGroup(key);
          group.raw.push({ input, value: input?.value || '', option });
          // Search & Discovery can temporarily expose overlapping stale values.
          // Max avoids inflated totals while its index catches up.
          group.count = Math.max(group.count, count);
        });
      });

      if (!groups.size) return;

      const rawContainer = platformGroup.querySelector('[data-ar-filter-options]');
      if (!rawContainer) return;
      rawContainer.classList.add('arCollection__legacyPlatformRaw');
      rawContainer.setAttribute('aria-hidden', 'true');

      platformGroup.querySelector('[data-ar-canonical-platform-options]')?.remove();

      const canonicalContainer = document.createElement('div');
      canonicalContainer.className = 'arCollection__options arCollection__canonicalOptions';
      canonicalContainer.setAttribute('data-ar-canonical-platform-options', '');

      const orderedGroups = Array.from(groups.values())
        .filter((group) => definitionMap.has(group.label))
        .sort((a, b) => a.index - b.index || a.label.localeCompare(b.label));

      const currentUrl = new URL(window.location.href);
      const stateFromUrl = currentUrl.searchParams.getAll('ar_platform');
      const selected = new Set(stateFromUrl.filter((key) => groups.has(key)));
      if (!selected.size) {
        rawOptions.forEach((option) => {
          const input = option.querySelector('input[type="checkbox"][data-ar-filter-input]');
          if (!input?.checked) return;
          const label = option.querySelector('.arCollection__optionLabel')?.textContent?.trim() || input.value;
          keysForLabel(label).forEach((key) => {
            if (groups.has(key)) selected.add(key);
          });
        });
      }

      const makeLogo = (group) => {
        if (!group.asset) return null;
        const wrap = document.createElement('span');
        wrap.className = 'arCollection__optionLogoWrap';
        wrap.setAttribute('aria-hidden', 'true');

        const img = document.createElement('img');
        img.className = 'arCollection__optionLogo' + (group.invert ? ' arCollection__optionLogo--invert' : '');
        img.src = group.asset;
        img.alt = '';
        img.width = 60;
        img.height = 32;
        img.loading = 'lazy';
        wrap.appendChild(img);
        return wrap;
      };

      const canonicalInputs = new Map();
      orderedGroups.forEach((group) => {
        const row = document.createElement('label');
        row.className = 'arCollection__option arCollection__option--platform arCollection__option--canonical';
        row.dataset.arFilterOption = '';
        row.dataset.filterLabel = group.label.toLowerCase();

        const input = document.createElement('input');
        input.type = 'checkbox';
        input.dataset.arCanonicalPlatform = group.label;
        input.checked = selected.has(group.label);

        const checkbox = document.createElement('span');
        checkbox.className = 'arCollection__checkbox';
        checkbox.innerHTML = '<svg viewBox="0 0 14 14" aria-hidden="true"><path d="m3 7 2.5 2.5L11 4" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>';

        const main = document.createElement('span');
        main.className = 'arCollection__optionMain';
        const logo = makeLogo(group);
        if (logo) main.appendChild(logo);

        const label = document.createElement('span');
        label.className = 'arCollection__optionLabel';
        label.textContent = group.label;
        main.appendChild(label);

        const count = document.createElement('span');
        count.className = 'arCollection__optionCount';
        count.textContent = String(group.count);

        row.append(input, checkbox, main, count);
        canonicalContainer.appendChild(row);
        canonicalInputs.set(group.label, input);
      });

      rawContainer.before(canonicalContainer);

      const platformName = rawInputs[0].name;

      const rebuildRawSelection = () => {
        const requiredValues = new Set();
        selected.forEach((key) => {
          groups.get(key)?.raw.forEach(({ value }) => requiredValues.add(value));
        });

        rawInputs.forEach((input) => {
          input.checked = requiredValues.has(input.value);
        });

        form?.querySelectorAll('[data-ar-platform-state]').forEach((input) => input.remove());
        selected.forEach((key) => {
          const state = document.createElement('input');
          state.type = 'hidden';
          state.name = 'ar_platform';
          state.value = key;
          state.dataset.arPlatformState = '';
          form?.appendChild(state);
        });

        canonicalInputs.forEach((input, key) => {
          input.checked = selected.has(key);
        });

        const summaryMeta = platformGroup.querySelector('.arCollection__summaryMeta');
        let summaryCount = summaryMeta?.querySelector('.arCollection__summaryCount');
        if (selected.size) {
          if (!summaryCount && summaryMeta) {
            summaryCount = document.createElement('span');
            summaryCount.className = 'arCollection__summaryCount';
            summaryMeta.prepend(summaryCount);
          }
          if (summaryCount) summaryCount.textContent = String(selected.size);
        } else {
          summaryCount?.remove();
        }
      };

      const urlForSelection = (keys) => {
        const url = new URL(window.location.href);
        url.searchParams.delete(platformName);
        url.searchParams.delete('ar_platform');
        url.searchParams.delete('page');

        const values = new Set();
        keys.forEach((key) => groups.get(key)?.raw.forEach(({ value }) => values.add(value)));
        values.forEach((value) => url.searchParams.append(platformName, value));
        keys.forEach((key) => url.searchParams.append('ar_platform', key));
        return url;
      };

      const updateGroupClear = () => {
        const clear = platformGroup.querySelector('.arCollection__groupClear');
        if (!clear) return;
        const url = new URL(window.location.href);
        url.searchParams.delete(platformName);
        url.searchParams.delete('ar_platform');
        url.searchParams.delete('page');
        clear.href = url.toString();
      };

      const updateActiveChips = () => {
        const track = root.querySelector('.arCollection__activeTrack');
        if (!track) return;

        track.querySelectorAll('[data-ar-canonical-platform-chip]').forEach((chip) => chip.remove());
        track.querySelectorAll('[data-ar-active-filter-param*="custom.platform"], [data-ar-active-filter-param*="custom.ar_filter_platform"]').forEach((chip) => {
          chip.hidden = true;
        });

        const clearAll = track.querySelector('.arCollection__clearAll');
        selected.forEach((key) => {
          const remaining = new Set(selected);
          remaining.delete(key);

          const chip = document.createElement('a');
          chip.className = 'arCollection__activeChip arCollection__activeChip--canonicalPlatform';
          chip.dataset.arCanonicalPlatformChip = key;
          chip.href = urlForSelection(remaining).toString();
          chip.append(document.createTextNode(key));

          const close = document.createElement('span');
          close.setAttribute('aria-hidden', 'true');
          close.textContent = '×';
          chip.appendChild(close);
          track.insertBefore(chip, clearAll || null);
        });
      };

      const serverRawActiveCount = rawInputs.filter((input) => input.checked).length;
      const updateOverallCount = () => {
        const badge = root.querySelector('.arCollection__filterCount');
        if (!badge) return;
        const original = Number.parseInt(badge.dataset.arOriginalCount || badge.textContent || '0', 10) || 0;
        if (!badge.dataset.arOriginalCount) badge.dataset.arOriginalCount = String(original);
        badge.textContent = String(Math.max(0, original - serverRawActiveCount + selected.size));
      };

      rebuildRawSelection();
      updateGroupClear();
      updateActiveChips();
      updateOverallCount();

      canonicalInputs.forEach((input, key) => {
        input.addEventListener('change', () => {
          if (input.checked) selected.add(key);
          else selected.delete(key);

          rebuildRawSelection();
          updateActiveChips();
          updateOverallCount();

          if (!mobile.matches) {
            window.clearTimeout(submitTimer);
            submitTimer = window.setTimeout(() => {
              setLoading();
              form?.requestSubmit();
            }, 180);
          }
        });
      });
    };

    canonicalizePlatformFilter();

    root.querySelectorAll('[data-ar-filter-search]').forEach((input) => {
      input.addEventListener('input', () => {
        const term = input.value.trim().toLowerCase();
        const group = input.closest('[data-ar-filter-group]');
        const scope = group?.querySelector('[data-ar-canonical-platform-options]') || group;
        scope?.querySelectorAll('[data-ar-filter-option]').forEach((option) => {
          option.hidden = Boolean(term) && !option.dataset.filterLabel.includes(term);
        });
      });
    });

    form?.addEventListener('change', (event) => {
      if (mobile.matches) return;
      if (!event.target.matches('input[type="checkbox"][data-ar-filter-input]')) return;
      window.clearTimeout(submitTimer);
      submitTimer = window.setTimeout(() => {
        setLoading();
        form.requestSubmit();
      }, 180);
    });

    form?.addEventListener('submit', setLoading);

    sort?.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sort.value);
      url.searchParams.delete('page');
      setLoading();
      window.location.assign(url.toString());
    });

    root.querySelectorAll('.arCollection__activeChip, .arCollection__clearAll, .arCollection__groupClear, .arCollection__footerClear, .arCollection__pagination a').forEach((link) => {
      link.addEventListener('click', setLoading);
    });
  };

  const initAll = () => document.querySelectorAll('[data-ar-collection]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();

  document.addEventListener('shopify:section:load', initAll);
})();
